import type { CartItem, Supplement } from './types';
export function getOptimizedImageUrl(url: string | null | undefined, width: number = 800, quality: number = 80): string {
  if (!url) return '';
  
  // Handle Supabase images
  if (url.includes('.supabase.co/storage/v1/object/public/')) {
    return url.replace('/object/public/', '/render/image/public/') + `?width=${width}&resize=contain&quality=${quality}`;
  }
  
  // Handle Unsplash images
  if (url.includes('images.unsplash.com')) {
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('w', width.toString());
      urlObj.searchParams.set('q', quality.toString());
      urlObj.searchParams.set('auto', 'format');
      return urlObj.toString();
    } catch {
      return url;
    }
  }
  
  return url;
}

// ---- Cart helpers (support Hype supplements / add-ons) ----

// Composite line id so the same item with different add-ons becomes a separate
// cart line, while the plain item keeps its real id (backward compatible).
export function buildCartLineId(itemId: string, supplements: Supplement[] = []): string {
  if (!supplements.length) return itemId;
  const sig = supplements.map((s) => `${s.id}:${s.quantity}`).sort().join(',');
  return `${itemId}|${sig}`;
}

// Per-item-unit cost of selected supplements.
export function supplementUnitCost(supplements: Supplement[] = []): number {
  return supplements.reduce((sum, s) => sum + s.price * s.quantity, 0);
}

// Full line total including supplements, multiplied by item quantity.
export const DELIVERY_ITEM_NAMES = ['delivery', 'доставка'];
export function isDeliveryItem(item: { title?: string; hype_name?: string }): boolean {
  const name = (item.hype_name || item.title || '').trim().toLowerCase();
  return DELIVERY_ITEM_NAMES.includes(name);
}
export function cartLineOriginalTotal(item: CartItem): number {
  const orig = item.originalPrice ?? item.price;
  return (orig + supplementUnitCost(item.supplements)) * item.quantity;
}
export function cartLineTotal(item: CartItem): number {
  return (item.price + supplementUnitCost(item.supplements)) * item.quantity;
}

// ---- Day + category discounts ----
export function getTodayStr(): string {
  const t = new Date();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return t.getFullYear() + '-' + m + '-' + d;
}

export function parseDiscounts(raw: string | undefined | null): import('./types').DiscountRule[] {
  if (!raw) return [];
  try {
    const a = JSON.parse(raw);
    return Array.isArray(a) ? a : [];
  } catch {
    return [];
  }
}

export function applyDiscount(price: number, type: import('./types').DiscountType, value: number): number {
  if (type === 'percent') return Math.max(0, price * (1 - value / 100));
  return Math.max(0, price - value);
}

export function discountsForToday(raw: string | undefined | null): Record<string, import('./types').DiscountRule> {
  const today = getTodayStr();
  const map: Record<string, import('./types').DiscountRule> = {};
  for (const d of parseDiscounts(raw)) {
    if (d.date === today && d.category) map[d.category] = d;
  }
  return map;
}
